import React from 'react';

interface StructuredDataProps {
  data: object;
}

/**
 * Component to add JSON-LD structured data to pages for better SEO
 * Helps Google understand your content for rich results
 */
export const StructuredData: React.FC<StructuredDataProps> = ({ data }) => {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

// Pre-built structured data schemas

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'TableChat',
  url: 'https://tablechat.io',
  logo: 'https://tablechat.io/logo512.png',
  description:
    'TableChat is an NFC-powered guest engagement platform for restaurants. TableChat enables real-time table-side messaging, automated promotions, VIP loyalty sign-up, and integrations with marketing tools like Klaviyo and Mailchimp — all without requiring guests to download an app.',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    email: 'support@tablechat.io',
    telephone: '+1-404-226-2614',
    url: 'https://tablechat.io/contact',
  },
  sameAs: [
    'https://tablechat.io',
    'https://www.instagram.com/tablechat.io',
    'https://www.linkedin.com/company/110435841',
    'https://x.com/ddhhayes',
  ],
};

export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'TableChat',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, iOS, Android',
  url: 'https://tablechat.io',
  brand: {
    '@type': 'Brand',
    name: 'TableChat',
  },
  offers: {
    '@type': 'Offer',
    price: '379.00',
    priceCurrency: 'USD',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '379.00',
      priceCurrency: 'USD',
      billingDuration: 'P1M',
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        value: '1',
        unitText: 'restaurant per month',
      },
    },
    availability: 'https://schema.org/InStock',
    url: 'https://tablechat.io/pricing',
  },
  description:
    'TableChat is a guest engagement platform for restaurants that combines NFC-powered table-side chat with automated promotions, VIP loyalty sign-up, and third-party marketing integrations (Klaviyo, Mailchimp). Guests tap an NFC tag to start a conversation — no app download required. Restaurants capture guest contact data, push daily specials and welcome promos, and build direct outreach loyalty programs.',
  featureList: [
    'NFC tap-to-connect guest messaging — no app download required',
    'Real-time server and kitchen communication',
    'Automated welcome promotions and daily specials',
    'VIP and loyalty program guest sign-up during the meal',
    'Guest contact data capture with opt-in consent',
    'Third-party marketing integrations (Klaviyo, Mailchimp, Constant Contact, and more)',
    'Multi-language automatic translation',
    'Daily operational reports emailed to management',
    'Custom branding per restaurant',
    'Unlimited staff accounts',
  ],
};

export const webSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'TableChat',
  url: 'https://tablechat.io',
  description:
    'TableChat is an NFC-powered guest engagement platform for restaurants. Real-time table-side messaging, automated promotions, VIP loyalty capture, and marketing integrations.',
  publisher: {
    '@type': 'Organization',
    name: 'TableChat',
    url: 'https://tablechat.io',
    logo: 'https://tablechat.io/logo512.png',
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://tablechat.io/faq?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

export const howToSetupSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Set Up TableChat for Your Restaurant',
  description:
    'Step-by-step guide to implementing TableChat NFC-powered guest engagement at your restaurant. The entire process typically takes approximately one week from signup to full operation.',
  totalTime: 'P7D',
  estimatedCost: {
    '@type': 'MonetaryAmount',
    currency: 'USD',
    value: '379',
  },
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Sign up and complete payment',
      text: 'Complete your signup and payment through the secure Stripe checkout process on the TableChat website.',
      url: 'https://tablechat.io/signup',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Receive your TableChat hardware',
      text: 'Receive your pre-configured NFC tags and kiosk tablet shipped directly to your restaurant.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Place NFC tags at tables',
      text: 'Place the pre-assigned NFC tags at each table in your restaurant. Tags are pre-configured and mapped to table numbers.',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Start receiving guest messages',
      text: 'Guests tap their phone on the NFC tag to open a browser-based chat. Messages appear on your kiosk tablet in real time with table number context. Staff tap to acknowledge and swipe to clear.',
    },
  ],
};

export const speakableSchema = (pageName: string, pageUrl: string, cssSelectors: string[]) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: pageName,
  url: pageUrl,
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: cssSelectors,
  },
});

export const breadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const faqSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const productSchema = (productName: string, price: number, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: productName,
  description: description,
  brand: {
    '@type': 'Brand',
    name: 'TableChat',
  },
  offers: {
    '@type': 'Offer',
    price: price.toFixed(2),
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: 'https://tablechat.io/pricing',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: price.toFixed(2),
      priceCurrency: 'USD',
      billingDuration: 'P1M',
      unitText: 'per restaurant per month',
    },
  },
});

export const messagingPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Guest Messaging - TableChat',
  url: 'https://tablechat.io/features/messaging',
  description:
    'TableChat lets restaurant guests tap an NFC tag to instantly message staff from their phone. No app download, no QR code. Requests reach your team in real time. Enhances service, drives guest engagement, and supports staff during every shift.',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['.feature-hero-text', '.hero-lead'],
  },
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  about: {
    '@type': 'SoftwareApplication',
    name: 'TableChat',
    applicationCategory: 'BusinessApplication',
    featureList: [
      'NFC tap-to-chat — guest opens a live web chat by tapping their phone to a table tag',
      'No app download, no QR code scan, no account creation required',
      'AI-powered chat assistant understands and categorizes guest requests',
      'Real-time message routing to staff kiosk tablet with table number context',
      'Multi-language automatic translation in both directions',
      'Staff see all active requests organized by table — tap to acknowledge, swipe to clear',
      'Multiple guests at the same table can chat simultaneously',
      'Works on iPhone (iOS 13+) and all modern Android devices',
      'Gateway to automated promotions, daily specials, and VIP loyalty enrollment',
    ],
  },
};

export const promotionsPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Guest Promotions - TableChat',
  url: 'https://tablechat.io/features/promotions',
  description:
    'Deliver welcome promotions, daily specials, and time-based offers directly to restaurant guests the moment they sit down using NFC-powered table-side messaging. No app download required.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  about: {
    '@type': 'SoftwareApplication',
    name: 'TableChat',
    applicationCategory: 'BusinessApplication',
    featureList: [
      'Automated welcome promotions delivered at the table',
      'Digital daily specials chalkboard — update from management console',
      'Time-based offers for happy hour, brunch, and late-night menus',
      'Zero printed materials — fully digital promotion delivery',
      '100% guest visibility — promos appear inside the active chat',
    ],
  },
};

export const loyaltyPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'VIP & Loyalty - TableChat',
  url: 'https://tablechat.io/features/loyalty',
  description:
    'Capture guest contact information and build loyalty lists during the meal with consent-based VIP opt-in. Turn first-time diners into repeat customers with verified contact capture and direct outreach.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  about: {
    '@type': 'SoftwareApplication',
    name: 'TableChat',
    applicationCategory: 'BusinessApplication',
    featureList: [
      'In-chat VIP opt-in during the meal',
      'Consent-based guest data capture (email or phone, verified with one-time code)',
      'Restaurant and table context recorded with each enrollment',
      'Consent timestamp recorded for compliance',
      'Data export to Klaviyo, Mailchimp, Constant Contact, and more',
    ],
  },
};

export const integrationsPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Integrations - TableChat',
  url: 'https://tablechat.io/features/integrations',
  description:
    'TableChat integrates with Klaviyo, Mailchimp, Constant Contact, and other marketing platforms to sync guest contact data captured at the table. No developer required.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  about: [
    {
      '@type': 'SoftwareApplication',
      name: 'Klaviyo',
      applicationCategory: 'MarketingApplication',
      description: 'Email and SMS marketing platform. TableChat syncs guest profiles to Klaviyo for segmented campaigns and automated flows.',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Mailchimp',
      applicationCategory: 'MarketingApplication',
      description: 'Email marketing and audience management. TableChat pushes guest contact data to Mailchimp audiences automatically.',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Constant Contact',
      applicationCategory: 'MarketingApplication',
      description: 'Email marketing and event promotions. TableChat syncs guest contact data to Constant Contact for direct marketing campaigns.',
    },
  ],
};

export const theDataPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'The Data - Guest Contact Value for Restaurants',
  url: 'https://tablechat.io/data',
  description:
    'Industry-verified data on the value of capturing guest contact information during the meal. Guests with captured contacts generate 12-18% more annual revenue. 95% of first-time diners never return without direct engagement. Interactive calculator shows the annual revenue asset in your guest traffic.',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['.feature-hero-text', '.stat-value', '.stat-label'],
  },
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  author: {
    '@type': 'Organization',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  about: {
    '@type': 'Dataset',
    name: 'Restaurant Guest Contact Value Benchmarks',
    description: 'Industry-verified benchmarks for the annual revenue value of captured guest contact information in restaurants.',
    creator: [
      { '@type': 'Organization', name: 'Deliverect' },
      { '@type': 'Organization', name: 'SevenRooms' },
      { '@type': 'Organization', name: 'Olo' },
      { '@type': 'Organization', name: 'Emarsys' },
      { '@type': 'Organization', name: 'Hootsuite' },
      { '@type': 'Organization', name: 'RivalIQ' },
    ],
  },
  citation: [
    { '@type': 'CreativeWork', name: 'Restaurant Guest Retention Study', author: { '@type': 'Organization', name: 'SevenRooms' } },
    { '@type': 'CreativeWork', name: 'Restaurant Industry Data Report', author: { '@type': 'Organization', name: 'Deliverect' } },
    { '@type': 'CreativeWork', name: 'Direct Ordering Consumer Behavior', author: { '@type': 'Organization', name: 'Olo' } },
    { '@type': 'CreativeWork', name: 'Customer Loyalty Index', author: { '@type': 'Organization', name: 'Emarsys' } },
  ],
};

export const smartAnswersPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Smart Answers - TableChat',
  url: 'https://tablechat.io/features/smart-answers',
  description:
    'TableChat answers guest questions about your menu, drinks, hours, allergens, and more — instantly, without involving your staff. Your restaurant\'s knowledge, available to every guest, every shift.',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['.feature-hero-text', '.hero-lead'],
  },
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  about: {
    '@type': 'SoftwareApplication',
    name: 'TableChat',
    applicationCategory: 'BusinessApplication',
    featureList: [
      'TableChat answers guest questions about menu, drinks, hours, and more',
      'Instant responses — no server involvement for common questions',
      'Always up to date — changes reflect across all tables within minutes',
      'Easy setup from the management console',
      'Specific to your restaurant — not generic responses',
    ],
  },
};

export const googleReviewsPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Google Reviews - TableChat',
  url: 'https://tablechat.io/features/google-reviews',
  description:
    'Grow your restaurant\'s positive Google reviews naturally. TableChat shares your Google review link when guests express genuine enthusiasm during the meal — no review gating, no staff effort required.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  about: {
    '@type': 'SoftwareApplication',
    name: 'TableChat',
    applicationCategory: 'BusinessApplication',
    featureList: [
      'Google review link shared naturally during guest chat',
      'Triggered by genuine positive sentiment — never pushed',
      'One-time setup via management console',
      'Fully compliant with Google review policies — no gating',
      'Review link shared at most once per guest session',
    ],
  },
};

export const whyTableChatPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'TableChat - NFC Guest Engagement Platform for Restaurants',
  url: 'https://tablechat.io/',
  description:
    'TableChat is an NFC-powered guest engagement platform that combines real-time table-side messaging, automated promotions, VIP loyalty capture, and marketing integrations. No app download required. $379/month per location.',
  speakable: {
    '@type': 'SpeakableSpecification',
    cssSelector: ['.feature-hero-text', '.hero-lead'],
  },
  isPartOf: {
    '@type': 'WebSite',
    name: 'TableChat',
    url: 'https://tablechat.io',
  },
  mainEntity: {
    '@type': 'SoftwareApplication',
    name: 'TableChat',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    url: 'https://tablechat.io',
    offers: {
      '@type': 'Offer',
      price: '379.00',
      priceCurrency: 'USD',
      billingDuration: 'P1M',
      availability: 'https://schema.org/InStock',
    },
    featureList: [
      'NFC tap-to-connect guest messaging — no app download required',
      'AI-powered chat assistant for guest requests',
      'Automated welcome promotions and daily specials',
      'Time-based promotional offers (happy hour, brunch, late-night)',
      'VIP opt-in guest data capture during the meal',
      'Verified guest contact capture with consent timestamp',
      'Klaviyo, Mailchimp, Constant Contact, and more integrations',
      'Multi-language automatic translation',
      'Daily operational reports emailed to management',
      'Custom branding per restaurant location',
      'Unlimited staff accounts',
      'No per-transaction or per-message fees',
    ],
  },
};

export default StructuredData;

